"use client";

import { c as _c } from "react/compiler-runtime";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import type { DropInteractionParams } from "@/components/waves/drops/Drop";
import { useEmoji } from "@/contexts/EmojiContext";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { DROP_POLL_VOTED_NOTIFICATION_CAUSE, type INotificationDropBoosted, type INotificationDropPollVoted, type INotificationDropReacted, type INotificationDropVoted, type NotificationPollVoteOption } from "@/types/feed.types";
import ReactionEmojiPreview from "./ReactionEmojiPreview";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationDrop from "../subcomponents/NotificationDrop";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { getIsDirectMessage, useWaveNavigation } from "../utils/navigationUtils";
import { formatSignedNotificationNumber, getNotificationRatingColor, isNotificationNumber } from "../utils/notificationRatingUtils";
export const getNotificationVoteColor = (vote: number) => {
  const color = getNotificationRatingColor(vote);
  return color === "tw-text-iron-400" ? "tw-text-iron-500" : color;
};
type NotificationUnion = INotificationDropVoted | INotificationDropPollVoted | INotificationDropReacted | INotificationDropBoosted;
interface Props {
  readonly notification: NotificationUnion;
  readonly activeDrop: ActiveDropState | null;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
}
function NotificationVoteInlinePart(t0) {
  const $ = _c(17);
  const {
    label,
    value,
    separator: t1,
    textClassName: t2
  } = t0;
  const separator = t1 === undefined ? "bullet" : t1;
  const textClassName = t2 === undefined ? "tw-text-sm" : t2;
  if (!isNotificationNumber(value)) {
    return null;
  }
  const t3 = separator === "arrow" ? `${textClassName} tw-font-bold tw-text-iron-400` : "tw-text-xs tw-font-bold tw-text-iron-400";
  const t4 = separator === "arrow" ? "\u2192" : "\u2022";
  let t5;
  if ($[0] !== t3 || $[1] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[0] = t3;
    $[1] = t4;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const t6 = `${textClassName} tw-font-normal tw-text-iron-400`;
  let t7;
  if ($[3] !== label || $[4] !== t6) {
    t7 = <span className={t6}>{label}:</span>;
    $[3] = label;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== value) {
    t8 = getNotificationVoteColor(value);
    $[6] = value;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = `${t8} ${textClassName} tw-font-medium tw-tabular-nums`;
  let t10;
  if ($[8] !== value) {
    t10 = formatSignedNotificationNumber(value);
    $[8] = value;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== t10 || $[11] !== t9) {
    t11 = <span className={t9}>{t10}</span>;
    $[10] = t10;
    $[11] = t9;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t11 || $[14] !== t5 || $[15] !== t7) {
    t12 = <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-whitespace-nowrap">{t5}{t7}{t11}</span>;
    $[13] = t11;
    $[14] = t5;
    $[15] = t7;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  return t12;
}
function getPollOptionText(options: readonly NotificationPollVoteOption[]): string | null {
  const optionText = options.map(option => option.option_string.trim()).filter(option => option.length > 0).join(", ");
  return optionText.length > 0 ? optionText : null;
}
export default function NotificationDropReacted(t0) {
  const $ = _c(74);
  const {
    notification,
    activeDrop,
    onReply,
    onDropContentClick
  } = t0;
  const {
    createReplyClickHandler,
    createQuoteClickHandler
  } = useWaveNavigation();
  const {
    findCustomEmoji,
    findNativeEmoji
  } = useEmoji();
  const isVoted = notification.cause === ApiNotificationCause.DropVoted;
  const isPollVoted = notification.cause === DROP_POLL_VOTED_NOTIFICATION_CAUSE;
  const isReacted = notification.cause === ApiNotificationCause.DropReacted;
  const isBoosted = notification.cause === ApiNotificationCause.DropBoosted;
  const drop = notification.related_drops[0];
  if (!drop) {
    return null;
  }
  let actionElement;
  if (isVoted) {
    const voteValue = notification.additional_context.vote;
    const voteChange = notification.additional_context.vote_change;
    const totalVote = notification.additional_context.total_vote;
    let t1;
    if ($[0] !== voteChange) {
      t1 = isNotificationNumber(voteChange);
      $[0] = voteChange;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const hasVoteChange = t1;
    const isInitialRating = !hasVoteChange || voteChange === voteValue;
    let t2;
    if ($[2] !== isInitialRating || $[3] !== voteChange || $[4] !== voteValue) {
      t2 = isInitialRating ? <><span className="tw-text-base tw-font-normal tw-text-iron-400">rated</span><span className={`${getNotificationVoteColor(voteValue)} ${"tw-text-base"} tw-font-medium tw-tabular-nums`}>{formatSignedNotificationNumber(voteValue)}</span></> : <><span className="tw-text-base tw-font-normal tw-text-iron-400">updated rating by</span><span className={`${getNotificationVoteColor(voteChange)} ${"tw-text-base"} tw-font-medium tw-tabular-nums`}>{formatSignedNotificationNumber(voteChange)}</span><NotificationVoteInlinePart label="New rating" value={voteValue} separator="arrow" textClassName="tw-text-base" /></>;
      $[2] = isInitialRating;
      $[3] = voteChange;
      $[4] = voteValue;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    let t3;
    if ($[6] !== totalVote) {
      t3 = <NotificationVoteInlinePart label="New Total" value={totalVote} textClassName="tw-text-base" />;
      $[6] = totalVote;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== notification.created_at) {
      t4 = <NotificationTimestamp createdAt={notification.created_at} className="tw-text-base" />;
      $[8] = notification.created_at;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
      t5 = <>{t2}{t3}{t4}</>;
      $[10] = t2;
      $[11] = t3;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    actionElement = t5;
  } else {
    if (isPollVoted) {
      let t1;
      if ($[14] !== notification.additional_context.poll_options) {
        t1 = getPollOptionText(notification.additional_context.poll_options);
        $[14] = notification.additional_context.poll_options;
        $[15] = t1;
      } else {
        t1 = $[15];
      }
      const pollOptionText = t1;
      let t2;
      if ($[16] !== pollOptionText) {
        t2 = pollOptionText ? <><span className="tw-text-sm tw-font-normal tw-text-iron-400">voted for</span><span className="tw-min-w-0 tw-max-w-full tw-break-words tw-text-sm tw-font-medium tw-text-iron-200">{pollOptionText}</span></> : <span className="tw-text-sm tw-font-normal tw-text-iron-400">voted in poll</span>;
        $[16] = pollOptionText;
        $[17] = t2;
      } else {
        t2 = $[17];
      }
      let t3;
      if ($[18] !== notification.created_at) {
        t3 = <NotificationTimestamp createdAt={notification.created_at} />;
        $[18] = notification.created_at;
        $[19] = t3;
      } else {
        t3 = $[19];
      }
      let t4;
      if ($[20] !== t2 || $[21] !== t3) {
        t4 = <>{t2}{t3}</>;
        $[20] = t2;
        $[21] = t3;
        $[22] = t4;
      } else {
        t4 = $[22];
      }
      actionElement = t4;
    } else {
      if (isBoosted) {
        let t1;
        if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">🔥 boosted 🔥</span>;
          $[23] = t1;
        } else {
          t1 = $[23];
        }
        let t2;
        if ($[24] !== notification.created_at) {
          t2 = <>{t1}<NotificationTimestamp createdAt={notification.created_at} /></>;
          $[24] = notification.created_at;
          $[25] = t2;
        } else {
          t2 = $[25];
        }
        actionElement = t2;
      } else {
        if (isReacted) {
          let rawId;
          let t1;
          if ($[26] !== findCustomEmoji || $[27] !== findNativeEmoji || $[28] !== notification.additional_context.reaction) {
            rawId = notification.additional_context.reaction.replaceAll(":", "");
            t1 = !findCustomEmoji(rawId) && !findNativeEmoji(rawId);
            $[26] = findCustomEmoji;
            $[27] = findNativeEmoji;
            $[28] = notification.additional_context.reaction;
            $[29] = rawId;
            $[30] = t1;
          } else {
            rawId = $[29];
            t1 = $[30];
          }
          if (t1) {
            return null;
          }
          let t2;
          if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
            t2 = <span className="tw-text-sm tw-font-normal tw-text-iron-400">reacted</span>;
            $[31] = t2;
          } else {
            t2 = $[31];
          }
          let t3;
          if ($[32] !== rawId) {
            t3 = <ReactionEmojiPreview rawId={rawId} />;
            $[32] = rawId;
            $[33] = t3;
          } else {
            t3 = $[33];
          }
          let t4;
          if ($[34] !== notification.created_at) {
            t4 = <NotificationTimestamp createdAt={notification.created_at} />;
            $[34] = notification.created_at;
            $[35] = t4;
          } else {
            t4 = $[35];
          }
          let t5;
          if ($[36] !== t3 || $[37] !== t4) {
            t5 = <>{t2}{t3}{t4}</>;
            $[36] = t3;
            $[37] = t4;
            $[38] = t5;
          } else {
            t5 = $[38];
          }
          actionElement = t5;
        } else {
          return null;
        }
      }
    }
  }
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[39] !== actionElement || $[40] !== activeDrop || $[41] !== createQuoteClickHandler || $[42] !== createReplyClickHandler || $[43] !== drop || $[44] !== isVoted || $[45] !== notification.related_identity || $[46] !== onReply) {
    const isDirectMessage = getIsDirectMessage(drop.wave);
    t6 = "tw-flex tw-w-full tw-flex-col tw-space-y-2";
    const t8 = isVoted ? "tw-text-base" : undefined;
    let t9;
    if ($[55] !== notification.related_identity) {
      t9 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
      $[55] = notification.related_identity;
      $[56] = t9;
    } else {
      t9 = $[56];
    }
    if ($[57] !== actionElement || $[58] !== notification.related_identity || $[59] !== t8 || $[60] !== t9) {
      t7 = <NotificationHeader author={notification.related_identity} authorClassName={t8} actions={t9}>{actionElement}</NotificationHeader>;
      $[57] = actionElement;
      $[58] = notification.related_identity;
      $[59] = t8;
      $[60] = t9;
      $[61] = t7;
    } else {
      t7 = $[61];
    }
    T0 = NotificationDrop;
    t1 = drop;
    t2 = activeDrop;
    t3 = onReply;
    t4 = createReplyClickHandler(drop.wave.id, isDirectMessage);
    t5 = createQuoteClickHandler(isDirectMessage);
    $[39] = actionElement;
    $[40] = activeDrop;
    $[41] = createQuoteClickHandler;
    $[42] = createReplyClickHandler;
    $[43] = drop;
    $[44] = isVoted;
    $[45] = notification.related_identity;
    $[46] = onReply;
    $[47] = T0;
    $[48] = t1;
    $[49] = t2;
    $[50] = t3;
    $[51] = t4;
    $[52] = t5;
    $[53] = t6;
    $[54] = t7;
  } else {
    T0 = $[47];
    t1 = $[48];
    t2 = $[49];
    t3 = $[50];
    t4 = $[51];
    t5 = $[52];
    t6 = $[53];
    t7 = $[54];
  }
  let t8;
  if ($[62] !== T0 || $[63] !== onDropContentClick || $[64] !== t1 || $[65] !== t2 || $[66] !== t3 || $[67] !== t4 || $[68] !== t5) {
    t8 = <T0 drop={t1} activeDrop={t2} onReply={t3} onReplyClick={t4} onQuoteClick={t5} onDropContentClick={onDropContentClick} />;
    $[62] = T0;
    $[63] = onDropContentClick;
    $[64] = t1;
    $[65] = t2;
    $[66] = t3;
    $[67] = t4;
    $[68] = t5;
    $[69] = t8;
  } else {
    t8 = $[69];
  }
  let t9;
  if ($[70] !== t6 || $[71] !== t7 || $[72] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[70] = t6;
    $[71] = t7;
    $[72] = t8;
    $[73] = t9;
  } else {
    t9 = $[73];
  }
  return t9;
}